'use client';

import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Paper,
  Collapse,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface BookmarkedMessage {
  id: string;
  content: string;
  timestamp?: string;
  role: 'assistant';
}

interface BookmarkedMessagesPanelProps {
  bookmarkedMessages: Map<string, BookmarkedMessage>;
  onRemoveBookmark: (messageId: string) => void;
  onJumpToMessage?: (messageId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: 320,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  borderLeft: `1px solid ${theme.palette.divider}`
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const MessageContent = styled(Box)(({ theme }) => ({
  p: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  cursor: 'pointer',
  transition: 'background-color 0.2s ease'
}));

const MessageText = styled(Typography)({
  fontSize: '0.875rem',
  lineHeight: 1.4,
  color: 'text.primary',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginBottom: '8px'
});

const TimestampChip = styled(Chip)({
  fontSize: '0.7rem',
  height: '20px',
  marginRight: '8px'
});

const BookmarkedMessagesPanel: React.FC<BookmarkedMessagesPanelProps> = ({
  bookmarkedMessages,
  onRemoveBookmark,
  onJumpToMessage,
  isOpen,
  onToggle
}) => {
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  const handleToggleExpand = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <StyledPaper elevation={1}>
      {/* 头部 */}
      <HeaderBox onClick={onToggle}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            标记消息
          </Typography>
          <Chip
            label={bookmarkedMessages.size}
            size="small"
            color="primary"
            sx={{ ml: 1, minWidth: '24px', height: '20px', fontSize: '0.75rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isOpen ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      </HeaderBox>

      {/* 消息列表 */}
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {bookmarkedMessages.size === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                height: '200px'
              }}
            >
              <BookmarkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                暂无标记消息
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                点击AI消息旁的书签图标来标记重要内容
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {Array.from(bookmarkedMessages.entries()).map(([messageId, message]) => {
                const isExpanded = expandedMessages.has(messageId);
                const displayContent = isExpanded ? message.content : truncateContent(message.content);

                return (
                  <ListItem key={messageId} sx={{ p: 0, flexDirection: 'column', alignItems: 'stretch' }}>
                    <MessageContent
                      onClick={() => onJumpToMessage && onJumpToMessage(messageId)}
                    >
                      {/* 消息头部信息 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BotIcon sx={{ fontSize: 16, color: 'secondary.main', mr: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          AI助手
                        </Typography>
                        {message.timestamp && (
                          <TimestampChip
                            label={formatTimestamp(message.timestamp)}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="展开/收起">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(messageId);
                              }}
                              sx={{ p: 0.5, mr: 0.5 }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="取消标记">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveBookmark(messageId);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* 消息内容 */}
                      <MessageText
                        variant="body2"
                        sx={{
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        {displayContent}
                      </MessageText>
                    </MessageContent>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Collapse>
    </StyledPaper>
  );
};

export default BookmarkedMessagesPanel;